/** Tokenized ReactMarkdown component map for chat assistant bubbles. */

import { writeRecipeUrl } from '../../utils/appNavigation.js'

function getText(children) {
  return (typeof children === 'string'
    ? children
    : (Array.isArray(children) ? children : [children])
        .flat()
        .map((x) =>
          typeof x === 'string'
            ? x
            : x?.props?.children != null
              ? getText(x.props.children)
              : ''
        )
        .join('')
  ).trim()
}

function getHeadingText(children) {
  return getText(children)
}

function isPantryMatchSummary(text) {
  return (
    /You have \d+ of (?:the )?\d+ ingredients(?: on hand)?(?:, including [^.]+)?\.?/i.test(text) ||
    /Protein ingredients: you have \d+ of \d+ on hand\.?/i.test(text) ||
    /Missing ingredients?:/i.test(text) ||
    /You(?:'|')re missing/i.test(text)
  )
}

function isLaterRecipeTitle(text) {
  return /^\s*#([2-9]|\d{2,})\.(\s|$)/.test(text)
}

function isFirstRecipeTitle(text) {
  return /^\s*#1\.\s/.test(text)
}

function headingBlock(Tag, baseSize, { children }) {
  const isLater = /^([2-9]\d*|[1-9]\d+)\.\s/.test(getHeadingText(children))
  const className = isLater
    ? 'chat-markdown-heading chat-markdown-recipe-divider'
    : 'chat-markdown-heading'
  return (
    <Tag className={className} style={{ fontSize: baseSize }}>
      {children}
    </Tag>
  )
}

/** Parse in-app recipe detail links from relative or absolute hrefs. */
export function parseRecipeDetailHref(href) {
  if (!href || typeof href !== 'string') return null
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://app.martinisbythelake.com'
    const url = new URL(href, base)
    const path = url.pathname.replace(/\/+$/, '') || '/'
    if (path !== '/recipes' && path !== '/cook') return null
    if (url.searchParams.get('tab') !== 'recipe') return null
    const id = url.searchParams.get('id')
    return id || null
  } catch {
    return null
  }
}

function openRecipeDetailFromChat(recipeId) {
  writeRecipeUrl({ tab: 'recipe', id: recipeId })
  window.dispatchEvent(new PopStateEvent('popstate'))
  window.dispatchEvent(
    new CustomEvent('agent:open-recipe-from-chat', { detail: { recipeId } })
  )
}

export const chatMarkdownComponents = {
  p: ({ children }) => {
    const text = getText(children)
    if (isPantryMatchSummary(text)) {
      return (
        <div className="chat-pantry-match-block">
          <span className="chat-pantry-match-badge">Pantry Match</span>
          <span className="chat-pantry-match-text">{children}</span>
        </div>
      )
    }
    if (isLaterRecipeTitle(text)) {
      return (
        <div className="chat-markdown-recipe-divider">
          <p className="chat-markdown-recipe-title">{children}</p>
        </div>
      )
    }
    if (isFirstRecipeTitle(text)) {
      return <p className="chat-markdown-recipe-title">{children}</p>
    }
    return <p className="chat-markdown-p">{children}</p>
  },
  strong: ({ children }) => <strong className="chat-markdown-strong">{children}</strong>,
  em: ({ children }) => <em className="chat-markdown-em">{children}</em>,
  ul: ({ children }) => <ul className="chat-markdown-ul">{children}</ul>,
  ol: ({ children }) => <ol className="chat-markdown-ol">{children}</ol>,
  li: ({ children }) => <li className="chat-markdown-li">{children}</li>,
  h1: ({ children }) => headingBlock('h1', '1.5em', { children }),
  h2: ({ children }) => headingBlock('h2', '1.3em', { children }),
  h3: ({ children }) => headingBlock('h3', '1.1em', { children }),
  h4: ({ children }) => <h4 className="chat-markdown-h4">{children}</h4>,
  h5: ({ children }) => <h5 className="chat-markdown-h5">{children}</h5>,
  h6: ({ children }) => <h6 className="chat-markdown-h6">{children}</h6>,
  code: ({ children, className }) => {
    const isInline = !className
    return isInline ? (
      <code className="chat-markdown-code-inline">{children}</code>
    ) : (
      <code className="chat-markdown-code-block">{children}</code>
    )
  },
  pre: ({ children }) => <pre className="chat-markdown-pre">{children}</pre>,
  blockquote: ({ children }) => (
    <blockquote className="chat-markdown-blockquote">{children}</blockquote>
  ),
  a: ({ href, children }) => {
    const recipeId = parseRecipeDetailHref(href)
    if (recipeId) {
      return (
        <a
          href={href}
          className="chat-markdown-link chat-markdown-recipe-link"
          onClick={(e) => {
            e.preventDefault()
            openRecipeDetailFromChat(recipeId)
          }}
        >
          {children}
        </a>
      )
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="chat-markdown-link"
      >
        {children}
      </a>
    )
  },
  hr: () => <hr className="chat-markdown-hr" />,
}
